/**
 * Facility Store - Context Switching for Indoor/Outdoor/Hybrid Facilities
 * 
 * This store manages the facility type and context switching functionality
 * that adapts the admin dashboard based on facility configuration.
 * 
 * Facility Types:
 * - indoor: Indoor-only facilities (simulators, bays, lessons)
 * - outdoor: Outdoor-only facilities (golf courses, tee times, carts)
 * - hybrid: Facilities with both indoor and outdoor offerings
 * 
 * For hybrid facilities, users can switch between indoor and outdoor contexts
 * to focus on specific operations while maintaining a unified dashboard view.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { FacilityInfo } from "@/types/golf";

interface FacilityState {
  // Facility information
  facilityId: string | null;
  facilityName: string;
  facilityType: FacilityType;
  facilityTimezone: string;
  
  // Current context (for hybrid facilities)
  currentContext: ContextType;
  
  // Loading state
  isLoading: boolean;
  isInitialized: boolean;
  
  // Actions
  setFacility: (facility: FacilityInfo) => void;
  switchContext: (context: ContextType) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
  
  // Computed helpers (implemented as getters in the state)
  isHybrid: () => boolean;
  isIndoorOnly: () => boolean;
  isOutdoorOnly: () => boolean;
  hasIndoor: () => boolean;
  hasOutdoor: () => boolean;
  shouldShowContextSwitcher: () => boolean;
}

const initialState = {
  facilityId: null,
  facilityName: "",
  facilityType: "indoor" as FacilityType,
  facilityTimezone: "America/Chicago",
  currentContext: "indoor" as ContextType,
  isLoading: false,
  isInitialized: false,
};

const useFacilityStore = create<FacilityState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setFacility: (facility: FacilityInfo) => {
        // When setting a new facility, determine the initial context
        let initialContext: ContextType = "indoor";
        
        if (facility.type === "outdoor") {
          initialContext = "outdoor";
        } else if (facility.type === "hybrid") {
          // For hybrid, keep the current context if valid, otherwise default to indoor
          const current = get().currentContext;
          initialContext = current;
        }
        
        set({
          facilityId: facility.id,
          facilityName: facility.name,
          facilityType: facility.type,
          facilityTimezone: facility.timezone || "America/Chicago",
          currentContext: initialContext,
          isInitialized: true,
          isLoading: false,
        });
      },

      switchContext: (context: ContextType) => {
        const { facilityType } = get();
        
        // Only allow switching for hybrid facilities
        if (facilityType !== "hybrid") {
          console.warn("Context switching is only available for hybrid facilities");
          return;
        }
        
        set({ currentContext: context });
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      reset: () => set(initialState),

      // Helper methods
      isHybrid: () => get().facilityType === "hybrid",
      isIndoorOnly: () => get().facilityType === "indoor",
      isOutdoorOnly: () => get().facilityType === "outdoor",
      hasIndoor: () => get().facilityType === "indoor" || get().facilityType === "hybrid",
      hasOutdoor: () => get().facilityType === "outdoor" || get().facilityType === "hybrid",
      shouldShowContextSwitcher: () => get().facilityType === "hybrid",
    }),
    {
      name: "teelogix-facility-store",
      partialize: (state) => ({
        // Only persist these fields
        facilityId: state.facilityId,
        facilityName: state.facilityName,
        facilityType: state.facilityType,
        facilityTimezone: state.facilityTimezone,
        currentContext: state.currentContext,
        isInitialized: state.isInitialized,
      }),
    }
  )
);

export default useFacilityStore;

// Selector hooks for common use cases
export const useFacilityType = () => useFacilityStore((state) => state.facilityType);
export const useCurrentContext = () => useFacilityStore((state) => state.currentContext);
export const useIsHybrid = () => useFacilityStore((state) => state.facilityType === "hybrid");
export const useHasOutdoor = () => useFacilityStore((state) => 
  state.facilityType === "outdoor" || state.facilityType === "hybrid"
);
export const useHasIndoor = () => useFacilityStore((state) => 
  state.facilityType === "indoor" || state.facilityType === "hybrid"
);
export const useShouldShowContextSwitcher = () => useFacilityStore((state) => 
  state.facilityType === "hybrid"
);

